import React from "react";
import { Button, Tooltip } from "@material-ui/core";
import { css } from "@emotion/css";
import RolloutStatusChip, { groupLabel } from "routes/healthForms/components/RolloutStatusChip";
import { deployAttribution } from "routes/healthForms/rolloutText";
import { ScheduleGroupRollout, ScheduleRollout, useDeploySchedule } from "routes/createContent/api/scheduleDeployments";
import { useHistory } from "react-router-dom";
import { Area } from "types";

const groupTooltip = (entry: ScheduleGroupRollout) => {
  if (entry.status === "inactive") return `Not deployed to ${groupLabel(entry.group)}`;
  const attribution = deployAttribution(entry);
  if (entry.status === "activating") return `Deployed ${attribution} — waiting for environments to converge`;
  return entry.has_undeployed_changes
    ? `${groupLabel(entry.group)} is serving the previously deployed state, deployed ${attribution}`
    : `Live on every ${groupLabel(entry.group)} environment — deployed ${attribution}`;
};

const servingStaleState = (entry: ScheduleGroupRollout | undefined) =>
  entry?.status === "active" && entry.has_undeployed_changes;

export const ScheduleDeploymentRow: React.VFC<{
  scheduleId: number;
  rollout: ScheduleRollout | undefined;
  showDeploy?: boolean;
}> = ({ scheduleId, rollout, showDeploy = false }) => {
  const deploy = useDeploySchedule();
  const history = useHistory();

  if (!rollout) {
    return null;
  }

  const prod = rollout.groups.find(entry => entry.group === "prod");
  const prodLive = prod?.status === "active" || prod?.status === "activating";
  const canDeploy = showDeploy && prod !== undefined && (!prodLive || servingStaleState(prod));

  return (
    <div className={styles.row}>
      {rollout.groups.map(entry => {
        const stale = servingStaleState(entry);
        const chip = (
          <RolloutStatusChip
            status={entry.status}
            label={groupLabel(entry.group)}
            tooltip={stale ? `${groupTooltip(entry)} — click to review` : groupTooltip(entry)}
            stale={stale}
          />
        );
        return stale ? (
          <button
            key={entry.group}
            type="button"
            className={styles.chipButton}
            onClick={event => {
              event.stopPropagation();
              history.push(
                Area.CreateContent_Templates_Changes.replace(":id", String(scheduleId)).replace(":group", entry.group)
              );
            }}
          >
            {chip}
          </button>
        ) : (
          <span key={entry.group}>{chip}</span>
        );
      })}
      {canDeploy && (
        <Tooltip
          title={
            prodLive
              ? "Release the edits made since the last prod deploy"
              : "Release this template's current state to prod"
          }
        >
          <span className={styles.action} onClick={event => event.stopPropagation()}>
            <Button
              size="small"
              color="primary"
              disabled={deploy.isLoading}
              onClick={() => deploy.mutate({ scheduleId, group: "prod" })}
            >
              Deploy to prod
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  `,
  action: css`
    margin-left: auto;
    white-space: nowrap;
  `,
  chipButton: css`
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
  `,
};
